import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import addYears from 'date-fns/addYears';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import parseISO from 'date-fns/parseISO';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import subSeconds from 'date-fns/subSeconds';
import subWeeks from 'date-fns/subWeeks';
import subYears from 'date-fns/subYears';
import { DateTime } from 'luxon';
import {
  ExpirationAttributes,
  ExpirationPeriod,
  ExpirationPeriodUnits,
  ExpirationType,
} from '../../gamification-api-client/types/expiration';
import { toEndOfDay } from '../../utils/date-utils';

// refDate is levelAcquiredAt for level expiration or transactionTimestamp for currency expiration
export function getNextExpirationDate(
  expirationConfig: ExpirationAttributes,
  timezone: string,
  refDate?: Date,
): Date | undefined {
  const now = subSeconds(new Date(), 1);
  const safeRefDate = !!refDate && isBefore(refDate, now) ? refDate : now;

  if (expirationConfig.type === ExpirationType.CALENDAR) {
    if (isAfter(new Date(expirationConfig.date), safeRefDate)) {
      return toEndOfDay(new Date(expirationConfig.date), timezone);
    }
    const expirationDate = parseISO(expirationConfig.date);
    const nextTime = nextTimeInFuture(expirationDate, expirationConfig.period, safeRefDate);
    return toEndOfDay(nextTime, timezone);
  }

  if (expirationConfig.type === ExpirationType.ROLLING) {
    const nextTime = nextTimeInFuture(safeRefDate, expirationConfig.period);
    return toEndOfDay(nextTime, timezone);
  }

  if (expirationConfig.type === ExpirationType.END_OF_MONTH) {
    return DateTime.fromJSDate(safeRefDate)
      .setZone(timezone || 'utc')
      .endOf('month')
      .toJSDate();
  }

  if (expirationConfig.type === ExpirationType.END_OF_WEEK) {
    return DateTime.fromJSDate(safeRefDate)
      .setZone(timezone || 'utc')
      .endOf('week')
      .toJSDate();
  }

  return undefined;
}

export function getLastExpirationDate(
  expirationConfig: ExpirationAttributes,
  levelAcquiredAt?: string,
): Date | undefined {
  const now = new Date();
  if (expirationConfig.type === ExpirationType.CALENDAR) {
    const expirationDate = parseISO(expirationConfig.date);
    return lastTimeInPast(expirationDate, expirationConfig.period);
  }
  if (expirationConfig.type === ExpirationType.ROLLING) {
    const expirationDate = levelAcquiredAt ? toDate(levelAcquiredAt) : now;
    return lastTimeInPast(expirationDate, expirationConfig.period);
  }
  if (expirationConfig.type === ExpirationType.END_OF_MONTH) {
    const expirationDate = subMonths(levelAcquiredAt ? toDate(levelAcquiredAt) : now, 1);
    return endOfMonth(expirationDate);
  }
  if (expirationConfig.type === ExpirationType.END_OF_WEEK) {
    const expirationDate = subWeeks(levelAcquiredAt ? toDate(levelAcquiredAt) : now, 1);
    return endOfWeek(expirationDate);
  }
  return undefined;
}

function toDate(date: string | Date): Date {
  if (typeof date === 'string') {
    return parseISO(date);
  }
  return date;
}

/* If date is in the past, adds period until date is in the future
 * If date is in the future, subtracts period until one more subtraction would make it in the past
 */
export function nextTimeInFuture(
  refDate: Date,
  period: ExpirationPeriod,
  now: Date = new Date(),
): Date {
  let nextTimeInFuture = refDate;

  if (isBefore(refDate, now)) {
    while (isBefore(nextTimeInFuture, now)) {
      nextTimeInFuture = addPeriod(nextTimeInFuture, period);
    }
  }

  if (isAfter(refDate, now)) {
    while (isAfter(nextTimeInFuture, now)) {
      nextTimeInFuture = subPeriod(nextTimeInFuture, period);
    }
    nextTimeInFuture = addPeriod(nextTimeInFuture, period);
  }
  return nextTimeInFuture;
}

/* If date is in the future, subtracts period until date is in the past
 * If date is in the past, adds period until one more addition would make it in the future
 */
export function lastTimeInPast(
  expirationConfDate: Date,
  period: ExpirationPeriod,
  now: Date = new Date(),
): Date {
  return subPeriod(nextTimeInFuture(expirationConfDate, period, now), period);
}

function addPeriod(date: Date, period: ExpirationPeriod): Date {
  switch (period.unit) {
    case ExpirationPeriodUnits.DAY:
      return addDays(date, period.value);
    case ExpirationPeriodUnits.WEEK:
      return addWeeks(date, period.value);
    case ExpirationPeriodUnits.MONTH:
      return addMonths(date, period.value);
    case ExpirationPeriodUnits.YEAR:
      return addYears(date, period.value);
  }
}

function subPeriod(date: Date, period: ExpirationPeriod): Date {
  switch (period.unit) {
    case ExpirationPeriodUnits.DAY:
      return subDays(date, period.value);
    case ExpirationPeriodUnits.WEEK:
      return subWeeks(date, period.value);
    case ExpirationPeriodUnits.MONTH:
      return subMonths(date, period.value);
    case ExpirationPeriodUnits.YEAR:
      return subYears(date, period.value);
  }
}
